import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, OperatorFunction, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import {
  ORBI_FIND_LAB_INSTRUCTIONS,
  ORBI_FIND_LAB_LOCATION_LABEL,
  ORBI_FIND_LAB_LOCATION_REQUIRED,
  ORBI_FIND_LAB_LOCATION_TOO_SHORT,
  ORBI_FIND_LAB_SERVICE_LABEL,
  ORBI_FIND_LAB_SERVICE_NOTE,
  ORBI_FIND_LAB_SERVICE_PLACEHOLDER,
  ORBI_FIND_LAB_SERVICE_REQUIRED,
  ORBI_FIND_LAB_SUBMIT_LABEL,
  ORBI_FIND_LAB_TITLE,
} from '@patient-ui/shared/constants';
import { OrbiFindLabData } from '@patient-ui/shared/models';

import {
  AddressLookupRow,
  AddressLookupService,
  MIN_SEARCH_TERM_LENGTH,
} from '../../../portal-appointments/services/address-lookup.service';
import {
  AvailableService,
  AvailableServicesService,
} from '../../../portal-appointments/services/available-services.service';
import { matchOrbiInitialService } from '../../services/orbi-find-lab-handoff.service';

/** One option in the card's "Reason for your visit" dropdown (`ui-select`). */
interface OrbiFindLabServiceItem {
  text: string;
  value: number;
}

/**
 * The search the user submitted from a Make an Appointment card (PCSA-13241).
 * Carries the service (id + display fields, so the host can seed the booking
 * session without re-fetching the catalog) and the location the search runs
 * against.
 */
export interface OrbiFindLabSearch {
  serviceId: number;
  serviceName: string;
  showUser: boolean;
  /** The raw ZIP/address text — passed through to the backend address search. */
  address: string;
  /** Present only when the agent resolved coordinates for the prefilled location. */
  latitude?: number;
  longitude?: number;
  /** Set only when the location text IS a bare 5-digit ZIP (see `resolveZipCode`). */
  zipCode?: string;
}

/**
 * Renders the "Make an Appointment" card in the Orbi chat thread (PCSA-13241).
 *
 * Data + copy mirror mobile (`orbi_chat_bubble.dart` →
 * `LabSearchOptionCard.makeAppointment`): the agent's `AppointmentFindLab`
 * payload PREFILLS the two search criteria, and the user completes/edits them
 * before searching. Every field on that payload is optional and `data: {}` is a
 * valid response (AC 3.2), so the card renders an empty, editable form in that
 * case rather than failing.
 *
 * The service dropdown is fed from the portal's own catalog
 * (`AvailableServicesService.getUserVisibleServices` — the same source the Find a
 * Lab modal uses), NOT from the agent: the agent may send a service NAME or a
 * catalog id in `initialService`, so both are matched against the catalog to
 * preselect an option (AC 4). An `initialService` that matches nothing leaves the
 * field empty for the user to choose.
 *
 * Presentational + self-validating only: on a valid submit it EMITS the search
 * (`search`). Seeding the booking session and routing into the booking flow are
 * the host thread's concern, the same split the Results card uses — the thread
 * also owns closing the Orbi panel first (mobile parity).
 *
 * The location field is a TYPEAHEAD over the portal's shared three-source
 * lookup (`AddressLookupService` — Algolia ZIPs, then SmartyStreets addresses,
 * with a Google forward-geocode only when both come back empty). It shipped as a
 * bare input, so no lookup ever fired and no suggestions could appear
 * (PCSA-13551); mobile's own card has had them from the start
 * (`LocationSearchExpandedContent` → `LocationSearchController`), and its code
 * names patient-web as the behaviour it mirrors. Sharing the service rather than
 * re-deriving the fan-out is what keeps the card, the Find a Lab modal and mobile
 * on one answer.
 *
 * The agent may additionally supply coordinates (`initialSearchLatitude` /
 * `Longitude`), which are forwarded when the prefilled location is submitted
 * unedited. Anything else the card can resolve comes from the suggestion the user
 * picked; a location with no suggestion behind it is passed through as typed for
 * the backend's `searchByAddress` to place, which is what the modal does too.
 */
@Component({
  selector: 'patient-ui-orbi-find-lab-card',
  templateUrl: './orbi-find-lab-card.component.html',
  styleUrls: ['./orbi-find-lab-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // One lookup per search field (see `AddressLookupService`) — a card in the
  // thread must not share an in-flight cache with the Find a Lab modal.
  providers: [AddressLookupService],
})
export class OrbiFindLabCardComponent implements OnInit, OnDestroy {
  /** The typed `AppointmentFindLab` payload from the turn (may be `{}`). */
  @Input() data: OrbiFindLabData | null = null;

  /** Emitted on a valid submit — the host seeds the session and navigates. */
  @Output() search = new EventEmitter<OrbiFindLabSearch>();

  /** Card copy (verbatim from mobile's make-an-appointment option card). */
  readonly title = ORBI_FIND_LAB_TITLE;
  readonly instructions = ORBI_FIND_LAB_INSTRUCTIONS;
  readonly serviceNote = ORBI_FIND_LAB_SERVICE_NOTE;
  readonly locationLabel = ORBI_FIND_LAB_LOCATION_LABEL;
  readonly serviceLabel = ORBI_FIND_LAB_SERVICE_LABEL;
  readonly submitLabel = ORBI_FIND_LAB_SUBMIT_LABEL;
  readonly servicePlaceholder = ORBI_FIND_LAB_SERVICE_PLACEHOLDER;

  searchForm!: FormGroup;

  /** The catalog options rendered in the service dropdown. */
  serviceItems: OrbiFindLabServiceItem[] = [];

  /** True while the service catalog request is in flight. */
  isLoadingServices = false;

  /** Inline validation messages, set on submit (AC 5). Null when valid. */
  locationErrorMessage: string | null = null;
  serviceErrorMessage: string | null = null;

  /**
   * The suggestion behind the current location text — the row the user picked,
   * or the one adopted on submit. Null while the text is just typed characters.
   */
  private selectedRow: AddressLookupRow | null = null;

  /** The rows the typeahead last settled on, for submit-time adoption. */
  private lastRows: AddressLookupRow[] = [];

  private readonly destroy$ = new Subject<void>();
  private services: AvailableService[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly availableServicesService: AvailableServicesService,
    private readonly addressLookup: AddressLookupService
  ) {}

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      location: [this.data?.initialLocation ?? '', Validators.required],
      serviceId: [null as number | null, Validators.required],
    });

    // Every completed lookup, so a submit can adopt the first row when the user
    // typed an address and never opened the list. `settled$` fires from inside
    // the shared source, so this holds even when the dropdown has closed.
    this.addressLookup.settled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ rows }) => {
        this.lastRows = rows;
      });

    // Clear each field's error as soon as the user acts on it.
    this.searchForm
      .get('location')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.locationErrorMessage = null;
        // Any edit invalidates both the cached suggestions and whatever row the
        // text used to stand for — the portal's modal drops the same two on
        // every keystroke, so an edited address can never keep the previous
        // pick's ZIP or coordinates. `onSelectSuggestion` re-arms `selectedRow`
        // AFTER writing the control, which is what makes its own write survive.
        this.selectedRow = null;
        this.lastRows = [];
        this.addressLookup.reset();
        this.cdr.markForCheck();
      });
    this.searchForm
      .get('serviceId')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.serviceErrorMessage = null;
        this.cdr.markForCheck();
      });

    this.loadServices();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * The service control, passed to `ui-select` as an explicit `[control]` (the
   * same wiring the portal's service selector uses) so the dropdown participates
   * in this card's form without a `formControlName` lookup.
   */
  get serviceControl(): UntypedFormControl {
    return this.searchForm.get('serviceId') as UntypedFormControl;
  }

  /**
   * The `[ngbTypeahead]` source for the location field (PCSA-13551) — the
   * portal's shared lookup, so this dropdown debounces, thresholds and orders
   * its rows exactly as the Find a Lab modal's does.
   */
  locationSuggestions: OperatorFunction<string, AddressLookupRow[]> = (
    searchText$: Observable<string>
  ) => this.addressLookup.typeahead(searchText$);

  /** Renders a suggestion row in the dropdown. */
  suggestionFormatter = (row: AddressLookupRow): string => row.text;

  /**
   * Adopt an explicitly picked suggestion. `preventDefault` stops ng-bootstrap
   * writing the raw ROW OBJECT into the control — the field holds display text,
   * so the row's `text` is written here instead and the row itself is kept for
   * the ZIP and coordinates it carries.
   */
  onSelectSuggestion(
    event: NgbTypeaheadSelectItemEvent<AddressLookupRow>
  ): void {
    event.preventDefault();
    const row = event.item;

    this.searchForm.get('location')?.setValue(row.text);
    // AFTER the write: `setValue` fires `valueChanges` synchronously, and that
    // handler clears `selectedRow` to drop a stale pick on an edit.
    this.selectedRow = row;
    this.cdr.markForCheck();
  }

  /**
   * Validate both criteria and emit the search (AC 5: the user must supply a
   * location AND a reason for visit before a search runs). Validation copy
   * matches the portal's Find a Lab modal so the two behave identically.
   */
  onSubmit(): void {
    // The user may have typed an address and searched without opening the
    // dropdown. Take the first suggestion the lookup already settled on, exactly
    // as the portal's modal does on a Search click (and mobile on submit/blur),
    // so a free-form address still searches against a canonical row.
    this.adoptFirstSuggestion();

    const location = (this.searchForm.get('location')?.value ?? '').trim();
    const serviceId = this.searchForm.get('serviceId')?.value as number | null;

    this.locationErrorMessage = !location
      ? ORBI_FIND_LAB_LOCATION_REQUIRED
      : location.length < MIN_SEARCH_TERM_LENGTH
      ? ORBI_FIND_LAB_LOCATION_TOO_SHORT
      : null;
    this.serviceErrorMessage = serviceId
      ? null
      : ORBI_FIND_LAB_SERVICE_REQUIRED;

    if (this.locationErrorMessage || this.serviceErrorMessage || !serviceId) {
      this.cdr.markForCheck();
      return;
    }

    const service = this.services.find((s) => s.serviceId === serviceId);
    const row = this.selectedRow;
    const zipCode = row?.zipcode?.trim() || this.resolveZipCode(location);
    const search: OrbiFindLabSearch = {
      serviceId,
      serviceName: service?.serviceName ?? '',
      showUser: service?.showUser ?? true,
      address: location,
    };
    // A picked suggestion is the most specific answer available — an Algolia ZIP
    // row and a Google fallback row both carry coordinates. Only if there is no
    // row do the agent's own coordinates apply, and only alongside the location
    // they belong to: an edited location must not inherit them.
    if (
      this.isUsableCoord(row?.latitude) &&
      this.isUsableCoord(row?.longitude)
    ) {
      search.latitude = row?.latitude;
      search.longitude = row?.longitude;
    } else if (
      location === (this.data?.initialLocation ?? '').trim() &&
      this.isUsableCoord(this.data?.initialSearchLatitude) &&
      this.isUsableCoord(this.data?.initialSearchLongitude)
    ) {
      search.latitude = this.data?.initialSearchLatitude;
      search.longitude = this.data?.initialSearchLongitude;
    }
    if (zipCode) {
      search.zipCode = zipCode;
    }

    this.search.emit(search);
  }

  /**
   * Promote the first cached suggestion to the location field when the user
   * typed but never picked one. No-op once a row is already adopted, when the
   * lookup settled on nothing, or when the text is empty — in which case the
   * typed string is submitted as-is for the backend to place.
   */
  private adoptFirstSuggestion(): void {
    const control = this.searchForm.get('location');
    const location = (control?.value ?? '').trim();
    const first = this.lastRows[0];

    if (this.selectedRow || !location || !first) {
      return;
    }

    control?.setValue(first.text);
    // AFTER the write, for the reason `onSelectSuggestion` documents.
    this.selectedRow = first;
  }

  /**
   * Load the user-visible service catalog and preselect the agent's
   * `initialService` when it matches (by catalog id, else by name).
   */
  private loadServices(): void {
    this.isLoadingServices = true;
    this.availableServicesService
      .getUserVisibleServices()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (services) => {
          this.services = services;
          this.serviceItems = services.map((service) => ({
            text: service.serviceName,
            value: service.serviceId,
          }));
          this.isLoadingServices = false;
          const preselected = this.matchInitialService(services);
          if (preselected !== null) {
            this.searchForm.get('serviceId')?.setValue(preselected);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          // The catalog is unavailable — leave the dropdown empty rather than
          // blocking the card; the user can still be routed by re-asking.
          this.isLoadingServices = false;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * The catalog id for the agent's `initialService` (by id, else by name — see
   * `matchOrbiInitialService`), or null to leave the field for the user.
   */
  private matchInitialService(services: AvailableService[]): number | null {
    return (
      matchOrbiInitialService(this.data?.initialService, services)?.serviceId ??
      null
    );
  }

  /**
   * The `zip` query param the booking search accepts, for text with no
   * suggestion behind it: set only when the location IS a bare 5-digit ZIP,
   * because a free-form address is the backend's to resolve rather than the
   * card's to guess at. A picked or adopted row supplies the ZIP directly and
   * takes precedence (`onSubmit`) — the same order the portal's modal uses.
   */
  private resolveZipCode(location: string): string | undefined {
    return /^\d{5}$/.test(location) ? location : undefined;
  }

  /** A coordinate is usable when finite and non-zero (0/0 means "not resolved"). */
  private isUsableCoord(value: number | undefined): boolean {
    return typeof value === 'number' && Number.isFinite(value) && value !== 0;
  }
}
